import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useGetKnowledgeBaseArticleBySlugQuery } from "knowledge-base/_api/knowledge-base-api";
import ArticleAttachments from "knowledge-base/articles/_views/article-attachments";
import ArticleMarkdown from "knowledge-base/articles/_views/article-markdown";
import { useKnowledgeBaseLocations } from "knowledge-base/knowledge-base-locations";
import { getSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import { Link as RouterLink } from "react-router";
import { useSelector } from "react-redux";

type Props = Readonly<{
  slug: string;
  /** Whose knowledge base this article is being read from — see ArticleBySlugArgs. */
  ownerLegalEntityId?: string;
  /**
   * Whether this knowledge base can be written at all, as the server reports it. Distinct from the
   * reader's own permission: a firm may author its own articles and still only read its client's
   * rules, and only an Attorney Hub administrator writes the public help.
   */
  canAuthor?: boolean;
}>;

export default function ArticleReader({ slug, ownerLegalEntityId, canAuthor }: Props) {
  const {
    data: article,
    isLoading,
    isError,
    error,
  } = useGetKnowledgeBaseArticleBySlugQuery({ slug, ownerLegalEntityId });
  const userPermissions = useSelector(getSessionPermissions);
  const { articleEditorPath } = useKnowledgeBaseLocations();

  // An affordance, not an authorization check: the server refuses an edit this reader may not
  // make, whatever the browser shows. Hiding the button for read-only users just avoids offering
  // an action that would fail.
  //
  // Both halves are needed. The permission says whether this person authors knowledge base content
  // for their own organization; canAuthor says whether this particular knowledge base is theirs to
  // write. Without the second, a firm reading its client's rules - and any Attorney Hub member who
  // is not an administrator, reading the public help - was offered an Edit button that the server
  // was always going to refuse.
  const canEdit = Boolean(userPermissions?.includesWriteAccessToKnowledgeBase) && canAuthor === true;

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !article) {
    // The server answers 404 for an article that does not exist and for one this reader may not
    // see, deliberately: telling them apart would confirm that a given page exists inside another
    // organization's wiki. So this message cannot promise which case it is.
    const isNotFound =
      typeof error === "object" && error !== null && "status" in error && error.status === 404;

    return (
      <Alert severity={isNotFound ? "info" : "error"} sx={{ m: 2 }}>
        <AlertTitle>{isNotFound ? "No such page" : "Could not load this page"}</AlertTitle>
        {isNotFound
          ? "This page does not exist, or it is not part of the knowledge base available to you."
          : "Something went wrong loading this page. Try again in a moment."}
      </Alert>
    );
  }

  return (
    <Box component="article" sx={{ maxWidth: 860 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 1 }}>
        <Typography component="h1" data-testid="article-title" variant="h4">
          {article.title}
        </Typography>
        {article.isDraft && <Chip color="warning" label="Draft" size="small" />}
        {article.isArchived && <Chip label="Archived" size="small" variant="outlined" />}
        <Box sx={{ flexGrow: 1 }} />
        {canEdit && (
          <Button
            component={RouterLink}
            size="small"
            startIcon={<EditOutlinedIcon />}
            to={articleEditorPath(article.slug)}
          >
            Edit
          </Button>
        )}
      </Stack>

      {article.isFormLetter && (
        <Alert icon={<EmailOutlinedIcon />} severity="info" sx={{ mb: 3 }}>
          <AlertTitle>This is a letter the application sends</AlertTitle>
          <Typography variant="body2">
            Editing this page changes what recipients read the next time it goes out. The subject
            line is <strong>{article.templateSubject}</strong>.
          </Typography>
        </Alert>
      )}

      <Divider sx={{ mb: 3 }} />

      <ArticleMarkdown>{article.body}</ArticleMarkdown>

      {/*
        Read-only here even for an author: this is the page as a reader sees it, and changing what
        hangs off it belongs in the editor. Attachments the reader may not open are still listed,
        as locked rows — see ArticleAttachments.
      */}
      {article.attachedDocumentIds.length > 0 && (
        <>
          <Divider sx={{ mt: 4, mb: 2 }} />
          <ArticleAttachments article={article} />
        </>
      )}

      {article.updated && (
        <>
          <Divider sx={{ mt: 4 }} />
          <Typography color="text.secondary" sx={{ mt: 2 }} variant="caption">
            Last updated {article.updated.toLocaleDateString()}
            {article.currentRevisionNumber ? ` · revision ${article.currentRevisionNumber}` : ""}
          </Typography>
        </>
      )}
    </Box>
  );
}
